import { getApiErrorDetail } from "@/api/errors";
import { useEffect, useState } from "react";
import { useGetAdminUsersQuery } from "@/api/api";
import { Button } from "../button";
import { Input } from "../input";
import { Skeleton } from "../skeleton";
import { Tabs, TabsList, TabsTrigger } from "../tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import AdminUserRow from "./AdminUserRow";
import AdminUserCreateDialog from "./AdminUserCreateDialog";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const COLUMNS = 10;

type SpecialFilter = "all" | "special" | "regular";

const SPECIAL_PARAM: Record<SpecialFilter, boolean | undefined> = {
  all: undefined,
  special: true,
  regular: false,
};

export default function AdminUserTable({
  onError,
}: {
  onError: (message: string | null) => void;
}) {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<SpecialFilter>("all");

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(input.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, isLoading, isFetching, error, refetch } = useGetAdminUsersQuery(
    {
      q: q || undefined,
      limit: PAGE_SIZE,
      offset,
      special: SPECIAL_PARAM[filter],
    },
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search by id, nickname, username, name or email"
          className="max-w-sm"
        />
        <Tabs
          value={filter}
          onValueChange={(value) => {
            setFilter(value as SpecialFilter);
            setOffset(0);
          }}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="special">Special</TabsTrigger>
            <TabsTrigger value="regular">Not special</TabsTrigger>
          </TabsList>
        </Tabs>
        {data && (
          <span className="text-sm text-muted-foreground">
            {data.total.toLocaleString()}{" "}
            {data.total === 1 ? "account" : "accounts"}
          </span>
        )}
        <div className="ml-auto">
          <AdminUserCreateDialog onError={onError} />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Handle</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Collections</TableHead>
            <TableHead className="text-right">Records</TableHead>
            <TableHead className="text-right">Media</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Last login</TableHead>
            <TableHead>Last seen</TableHead>
            <TableHead className="w-16" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={COLUMNS}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : error && !data ? (
            <TableRow>
              <TableCell colSpan={COLUMNS} className="text-center">
                <span className="text-destructive">
                  {getApiErrorDetail(
                    error,
                    "The accounts could not be loaded.",
                  )}
                </span>{" "}
                <button
                  type="button"
                  className="underline underline-offset-2 text-muted-foreground hover:text-foreground"
                  onClick={() => refetch()}
                >
                  Try again
                </button>
              </TableCell>
            </TableRow>
          ) : data && data.items.length > 0 ? (
            data.items.map((user) => (
              <AdminUserRow key={user.id} user={user} onError={onError} />
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={COLUMNS}
                className="text-center text-muted-foreground"
              >
                No users found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)} of{" "}
            {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= data.total || isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
